// @ts-strict-ignore
import * as React from 'react';
import { CSSProperties } from 'react';
import Drawer from 'antd/lib/drawer';
import {
    AppStoreAvailableBuild,
    AssociatedStabilityIssue,
    AutomationType,
    CIBuildInfo,
    CodeItem as CodeItemType,
    CommitItem,
    FeatureItem,
    FeatureItemCodeStatus,
    IntegrationInstallationId,
    IntegrationType,
    LifecycleStep,
    mapCodeStatus,
    PRItem,
    PullRequestStackInfo,
    ReleaseCandidateBuildInfo,
    TicketItem,
} from '../../types';
import { antGrey, drawerSectionSeparator, runwayColors, tagStyle, titleStyle } from '../styles/globalStyles';
import Text from 'antd/lib/typography/Text';
import Link from 'antd/lib/typography/Link';
import Divider from 'antd/lib/divider';
import Space from 'antd/lib/space';
import Tooltip from 'antd/lib/tooltip';
import {
    colorForFeatureAffiliationTags,
    copyToClipboard,
    displayNameForFeatureAffiliation,
    getBuildsWithStabilityInfo,
    mergeQueueStateForCodeItem,
    tagColorForFeatureItemCodeStatus,
    tagColorForStatusCategory,
} from '../../helpers';
import { iconForStatus, isCommit, stackInfoTooltipText } from './featureReadinessHelpers';
import StackedIcon from '../icons/StackedIcon';
import { AICoAuthorDetailRow, getPercentageAICoAuthoredForCodeItem } from './aiCoAuthorDisplayHelpers';
import Tag from 'antd/lib/tag';
import IntegrationAvatar from '../IntegrationAvatar';
import {
    ArrowRightOutlined,
    BugFilled,
    CopyOutlined,
    DiffOutlined,
    InfoCircleOutlined,
    MinusOutlined,
    PlusOutlined,
    PullRequestOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import {
    selectAppDistributionServices,
    selectAllAppIdentifiers,
    selectAppTimeFormatSetting,
    selectAutomationById,
    selectEffectiveTimezone,
    selectIsRunwayOrg,
    selectIsWorkflowsCIStepEnabled,
    selectRelease,
    selectRequiresFixRequestsApprovals,
    selectStabilityIntegrations,
} from '../../selectors/selectors';
import { RootState } from '../../reducers/reducers';
import FeatureItemBuild from './FeatureItemBuild';
import { FixRequestSection } from '../triage/FixRequestSection';
import Paragraph from 'antd/lib/typography/Paragraph';
import Button from 'antd/lib/button';
import MarkdownComponent from '../HOCs/Markdown';
import { dayjs } from '@/extendedDayjs';
import { convertTo24HourFormat, DatetimeFormat } from '../../timeHelpers';
import IntegrationIcon from '../icons/IntegrationIcon';
import Alert from 'antd/lib/alert';
import Collapse from 'antd/lib/collapse';
import List from 'antd/lib/list';
import { useLifecycleStepCompleted } from '../automations/automationHelperComponents';
import AppStoreBuild from '../appStoreBuilds/AppStoreBuildComponent';
import { updateFeatureItemsFilters } from '../../actions/filters';
import { AnyFilter, FilterType } from '../../types/filters';
import { Nullable, ReleaseComponentProps, StepRunComponentProps } from '../../typescriptHelpers';
import { codeStyle, displayCodeStatusForStatus } from '../../helpers/displayHelpers';
import TimeWithConditionalTz from '@/components/common/TimeWithConditionalTz';

interface FeatureItemDrawerProps extends ReleaseComponentProps {
    isVisible: boolean;
    isOnWorkingBranch: boolean;
    selectedItem: FeatureItem;
    closeDrawer: () => void;
    vcsId?: IntegrationType;
    featureManagementId?: IntegrationType;
    stabilityInstallationIds?: IntegrationInstallationId[];
    onApplyFilters: (filters: AnyFilter[]) => void;
    userId: string;
    afterOpenChange: (open: boolean) => void;
    stepRunId: string;
    latestStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    selectedStagingBuild: Nullable<ReleaseCandidateBuildInfo>;
    latestWorkingBranchBuild: Nullable<CIBuildInfo>;
    appStoreSelectedBuilds: Nullable<AppStoreAvailableBuild[]>;
    autoUploadDsymsToStabilityMonitoringAutomationId: Nullable<string>;
    autoUploadBinaryArtifactToAppStoreAutomationId: Nullable<string>;
    autoDownloadArtifactsAutomationId: Nullable<string>;
    isAutoMergeRunwayPRsEnabled: boolean;
    requiresFixRequestsApprovals?: boolean;
    stepRunComponentProps: Nullable<StepRunComponentProps>;
    onVersionMoveStarted: (fixRequestId: string, targetVersion: string) => void;
}

const FeatureItemDrawer: React.FC<FeatureItemDrawerProps> = (props: FeatureItemDrawerProps) => {
    const { isVisible, selectedItem, closeDrawer, vcsId, featureManagementId, stabilityInstallationIds } = props;

    const latestStagingBuild = props.latestStagingBuild;
    const selectedStagingBuild = props.selectedStagingBuild;
    const latestWorkingBranchBuild = props.latestWorkingBranchBuild;

    const appStoreSelectedBuilds = props.appStoreSelectedBuilds;
    const buildsWithStabilityInfo = getBuildsWithStabilityInfo(appStoreSelectedBuilds);

    const distributionServices = useSelector((state: RootState) => selectAppDistributionServices(state, props.appId));
    const appStabilityIntegrations = useSelector((state: RootState) => selectStabilityIntegrations(state, props.appId));
    const appIdentifiers = useSelector((state: RootState) => selectAllAppIdentifiers(state, props.appId));
    const version = useSelector((state: RootState) => selectRelease(state, props.releaseId)).version;

    const artifactAutomations = useSelector((state: RootState) => {
        return {
            [AutomationType.AutoUploadDsymsToStabilityMonitoring]: selectAutomationById(
                state,
                props.appId,
                props.autoUploadDsymsToStabilityMonitoringAutomationId,
            ),

            [AutomationType.AutoUploadBinaryArtifactToAppStore]: selectAutomationById(
                state,
                props.appId,
                props.autoUploadBinaryArtifactToAppStoreAutomationId,
            ),
            [AutomationType.AutoDownloadArtifacts]: selectAutomationById(
                state,
                props.appId,
                props.autoDownloadArtifactsAutomationId,
            ),
        };
    });
    const isRunwayOrg = useSelector((state: RootState) => selectIsRunwayOrg(state));

    const appRequiresFixRequestsApprovals = useSelector(selectRequiresFixRequestsApprovals(props.appId));
    const requiresFixRequestsApprovals = props.requiresFixRequestsApprovals ?? appRequiresFixRequestsApprovals;

    const isWorkflowsCIStepEnabled = useSelector((state: RootState) =>
        selectIsWorkflowsCIStepEnabled(state, props.appId),
    );

    const use24HourTimeFormat = useSelector((state: RootState) => selectAppTimeFormatSetting(state, props.appId));
    const effectiveTimezone = useSelector((state: RootState) => selectEffectiveTimezone(state, props.appId));

    if (!selectedItem) return null;

    return (
        <Drawer
            onClose={closeDrawer}
            open={isVisible}
            closable={false}
            bodyStyle={{ padding: 0, paddingBottom: 85, overflowX: 'hidden' }}
            width={680}
            destroyOnClose={true}
            afterOpenChange={props.afterOpenChange}
        >
            {selectedItem.fixRequest && (
                <FixRequestSection
                    orgId={props.orgId}
                    onVersionMoveStarted={(targetVersion) =>
                        props.onVersionMoveStarted(selectedItem.fixRequest.id, targetVersion)
                    }
                    // Done items are settled: no version move or status actions, like closed
                    // issues in triage (whose inertness comes from state gating).
                    disabled={selectedItem.isItemDone}
                    featureReadiness={{
                        appId: props.appId,
                        releaseId: props.releaseId,
                        stepRunId: props.stepRunId,
                        fixRequest: selectedItem.fixRequest,
                        featureItemId: selectedItem.id,
                        isAutoIgnoreItem: selectedItem.autoIgnored,
                        requiresFixRequestsApprovals,
                    }}
                />
            )}
            <TicketInfo
                ticket={selectedItem.ticket}
                featureManagementId={featureManagementId}
                drawerIsVisible={isVisible}
                use24HourTimeFormat={use24HourTimeFormat}
                effectiveTimezone={effectiveTimezone}
            />
            {selectedItem.groupedCodeItems?.map((groupedCodeItem, i) => (
                <CodeInfo
                    key={i}
                    code={groupedCodeItem.code}
                    isOnWorkingBranch={props.isOnWorkingBranch}
                    vcsId={vcsId}
                    drawerIsVisible={isVisible}
                    isAutoMergeRunwayPRsEnabled={props.isAutoMergeRunwayPRsEnabled}
                    groupedCodeItems={selectedItem.groupedCodeItems}
                    use24HourTimeFormat={use24HourTimeFormat}
                    effectiveTimezone={effectiveTimezone}
                />
            ))}
            {!selectedItem.groupedCodeItems && (
                <CodeInfo
                    code={selectedItem.code}
                    isOnWorkingBranch={props.isOnWorkingBranch}
                    vcsId={vcsId}
                    drawerIsVisible={isVisible}
                    isAutoMergeRunwayPRsEnabled={props.isAutoMergeRunwayPRsEnabled}
                    use24HourTimeFormat={use24HourTimeFormat}
                    effectiveTimezone={effectiveTimezone}
                />
            )}
            <FeatureItemBuild
                appId={props.appId}
                orgId={props.orgId}
                releaseId={props.releaseId}
                version={version}
                latestOrSelectedStagingBuild={selectedStagingBuild ?? latestStagingBuild}
                latestWorkingBranchBuild={latestWorkingBranchBuild}
                isInLatestOrSelectedStagingBuild={selectedItem.isInLatestOrSelectedStagingBuild}
                isInLatestWorkingBranchBuild={selectedItem.isInLatestWorkingBranchBuild}
                services={distributionServices}
                stabilityIntegrations={appStabilityIntegrations}
                appIdentifiers={appIdentifiers}
                artifactsAutomations={artifactAutomations}
                isRunwayOrg={isRunwayOrg}
                isWorkflowsCIStepEnabled={isWorkflowsCIStepEnabled}
                stepRunComponentProps={props.stepRunComponentProps}
                use24HourTimeFormat={use24HourTimeFormat}
            />
            <StabilityInfo
                stabilityInstallationIds={stabilityInstallationIds}
                frItem={selectedItem}
                buildsWithStabilityInfo={buildsWithStabilityInfo}
                releaseId={props.releaseId}
                appId={props.appId}
                closeDrawer={closeDrawer}
                onApplyFilters={props.onApplyFilters}
            />
            <CollapsibleSection
                isLast
                label={
                    <SectionHeader
                        header="Info"
                        customIcon={<InfoCircleOutlined style={{ color: antGrey.grey7, marginRight: '-2px' }} />}
                    />
                }
            >
                <Text>{selectedItem.explanationText}</Text>
            </CollapsibleSection>
        </Drawer>
    );
};

interface TicketInfoProps {
    ticket?: TicketItem;
    featureManagementId?: IntegrationType;
    drawerIsVisible: boolean;
    use24HourTimeFormat: boolean;
    effectiveTimezone: string;
}

const TicketInfo = (props: TicketInfoProps) => {
    const { ticket, featureManagementId, drawerIsVisible, use24HourTimeFormat, effectiveTimezone } = props;

    if (!ticket) {
        return (
            <CollapsibleSection label={<SectionHeader header="Ticket" integrationId={featureManagementId} />}>
                <Text type="secondary">No ticket</Text>
            </CollapsibleSection>
        );
    }

    const status = ticket.status;
    const bgColor = tagColorForStatusCategory(ticket.statusCategory);
    const title = ticket.description;
    const description = ticket.fullDescription;
    const owner = ticket.owner;
    const reporter = ticket.reporter;
    const project = ticket.project;
    const featureAffiliations = ticket.featureAffiliations || [];
    const customFields = ticket.additionalFields;

    return (
        <CollapsibleSection
            label={<SectionHeader header="Ticket" integrationId={featureManagementId} />}
            extra={
                <Tag color={bgColor} style={{ ...tagStyle, fontSize: 10, marginInlineEnd: 0 }}>
                    {status}
                </Tag>
            }
        >
            {project ?
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Link href={project.url} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                            {ticket.project.displayableName}
                        </Link>
                        <Link href={ticket.url} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                            {ticket.displayableIdentifier ?? ticket.identifier}
                        </Link>
                    </div>
                    <Text style={{ fontSize: '16px', overflowWrap: 'anywhere' }}>{title}</Text>
                </div>
            :   <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Text style={{ fontSize: '16px' }}>{title}</Text>
                    <Link href={ticket.url} target="_blank" style={{ whiteSpace: 'nowrap' }}>
                        {ticket.displayableIdentifier ?? ticket.identifier}
                    </Link>
                </div>
            }
            <Divider style={{ margin: '16px 0px' }} />
            {featureAffiliations.length > 0 && (
                <Space size={[12, 8]} style={{ flexWrap: 'wrap', marginBottom: '24px' }}>
                    {featureAffiliations.map((fa, i) => {
                        return (
                            <Tooltip
                                key={`${fa.integrationId}-${i}`}
                                title={displayNameForFeatureAffiliation(fa.affiliationType)}
                            >
                                <Tag
                                    color={colorForFeatureAffiliationTags(fa.affiliationType)}
                                    style={{
                                        color: antGrey.grey8,
                                        fontSize: '14px',
                                        lineHeight: '22px',
                                        height: '30px',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                >
                                    <span>{fa.resolvedString}</span>
                                </Tag>
                            </Tooltip>
                        );
                    })}
                </Space>
            )}
            {description && <ExpandableDescription description={description} drawerIsVisible={drawerIsVisible} />}
            {owner && <Person title="Assignee" user={owner} />}
            {reporter && <Person title="Reporter" user={reporter} />}
            {customFields && Object.keys(customFields).length > 0 && (
                <div>
                    {Object.keys(customFields).map((fieldName) => {
                        return <CustomField name={fieldName} value={customFields[fieldName]} key={fieldName} />;
                    })}
                </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {ticket.createdAt && (
                    <Text type="secondary">
                        Created{' '}
                        {dayjs(ticket.createdAt)
                            .tz(effectiveTimezone)
                            .format(
                                use24HourTimeFormat ?
                                    convertTo24HourFormat(DatetimeFormat.LongMonthDateTime)
                                :   DatetimeFormat.LongMonthDateTime,
                            )}
                    </Text>
                )}
                {ticket.updatedAt && (
                    <Text type="secondary">
                        Updated{' '}
                        {dayjs(ticket.updatedAt)
                            .tz(effectiveTimezone)
                            .format(
                                use24HourTimeFormat ?
                                    convertTo24HourFormat(DatetimeFormat.LongMonthDateTime)
                                :   DatetimeFormat.LongMonthDateTime,
                            )}
                    </Text>
                )}
            </div>
        </CollapsibleSection>
    );
};

interface CodeInfoProps {
    code?: CommitItem | PRItem;
    vcsId?: IntegrationType;
    drawerIsVisible: boolean;
    isAutoMergeRunwayPRsEnabled: boolean;
    isOnWorkingBranch: boolean;
    groupedCodeItems?: FeatureItem[];
    use24HourTimeFormat: boolean;
    effectiveTimezone: string;
}

const CodeInfo = (props: CodeInfoProps) => {
    const { code, vcsId, drawerIsVisible, use24HourTimeFormat, effectiveTimezone } = props;

    if (!code) {
        return (
            <CollapsibleSection label={<SectionHeader header="Code" integrationId={vcsId} />}>
                <Text type="secondary">No code</Text>
            </CollapsibleSection>
        );
    }

    const status = mapCodeStatus(code.status);
    const mergeQueueState = mergeQueueStateForCodeItem(code);
    const bgColor = tagColorForFeatureItemCodeStatus(status, mergeQueueState);
    const title = isCommit(code) ? code.message : code.title;
    const description = !isCommit(code) ? code.description : null;
    const codeChanges = !isCommit(code) ? code.changesVolume : null;
    const author = code.author;
    const prItem = code as PRItem;
    const commitItem = code as CommitItem;

    const codeChangeTooltipText = () => {
        const codeDiffs = [
            {
                id: 'added',
                icon: <PlusOutlined />,
                displayText: `Lines added:`,
                value: `${codeChanges.added}`,
            },
            {
                id: 'deleted',
                icon: <MinusOutlined />,
                displayText: `Lines removed:`,
                value: `${codeChanges.deleted}`,
            },
            {
                id: 'changed',
                icon: <DiffOutlined />,
                displayText: `Lines changed:`,
                value: `${codeChanges.changed}`,
            },
        ];
        return (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {codeDiffs.map((diff) => {
                    return (
                        <div
                            key={diff.id}
                            style={{
                                display: 'flex',
                                gap: '10px',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                            }}
                        >
                            {diff.icon}
                            <Text style={{ marginRight: 'auto', color: 'white' }}>{diff.displayText}</Text>
                            <Text style={{ color: 'white' }}>{diff.value}</Text>
                        </div>
                    );
                })}
            </div>
        );
    };

    const containerStyle: CSSProperties = {
        backgroundColor: antGrey.grey2,
        borderRadius: '8px',
        padding: '12px',
        position: 'relative',
    };

    const headerStyle: CSSProperties = {
        color: antGrey.grey8,
        fontWeight: 'bold',
        fontSize: '15px',
    };

    const manualCherryPickBlock = () => {
        if (code.manualCherryPickCommands && code.manualCherryPickCommands?.length > 0) {
            return (
                <>
                    <br />
                    <Alert
                        type="error"
                        showIcon={true}
                        style={{
                            color: antGrey.grey8,
                            fontWeight: 'normal',
                            fontSize: '12px',
                        }}
                        message={
                            code.manualCherryPickContext ?? 'An error prevented Runway from cherry-picking this code'
                        }
                    ></Alert>
                    <div style={containerStyle}>
                        <h2 style={headerStyle}>Use these commands to cherry-pick manually:</h2>
                        <Button
                            onClick={() => copyToClipboard(code.manualCherryPickCommands?.join('\n'))}
                            icon={<CopyOutlined style={{ color: antGrey.grey6 }} />}
                            type="link"
                            style={{ width: 28, position: 'absolute', right: 10, top: 10, zIndex: 0 }}
                        />
                        <Text code style={codeStyle('12px')}>
                            {code.manualCherryPickCommands?.map((command) => {
                                return <div key={command}>{command}</div>;
                            })}
                        </Text>
                    </div>
                </>
            );
        } else {
            return null;
        }
    };

    let headerText = 'Code';
    if (props.groupedCodeItems) {
        headerText +=
            ' (' +
            (props.groupedCodeItems.findIndex((item) => item.code.identifier == props.code.identifier) + 1) +
            '/' +
            props.groupedCodeItems.length +
            ')';
    }

    return (
        <CollapsibleSection
            label={<SectionHeader header={headerText} integrationId={vcsId} />}
            extra={
                <Tag color={bgColor} style={{ ...tagStyle, fontSize: 10 }}>
                    {displayCodeStatusForStatus(status, props.isOnWorkingBranch, mergeQueueState)}
                </Tag>
            }
        >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <Text style={{ fontSize: '16px', overflowWrap: 'anywhere', marginRight: 'auto' }}>{title}</Text>
                {codeChanges && (
                    <div style={{ display: 'flex', alignItems: 'center', marginLeft: '10px' }}>
                        <Tooltip placement="left" title={codeChangeTooltipText}>
                            <DiffOutlined />
                        </Tooltip>
                        <Link
                            href={codeChanges.codeDiffUrl}
                            target="_blank"
                            style={{ whiteSpace: 'nowrap', marginLeft: '6px' }}
                        >
                            {codeChanges.changed}
                        </Link>
                    </div>
                )}
            </div>
            {manualCherryPickBlock()}
            <Divider style={{ margin: '16px 0px' }} />
            {description && <ExpandableDescription description={description} drawerIsVisible={drawerIsVisible} />}
            {author && <Person title={'Owner'} user={author} />}
            {!!code.aiCoAuthors?.length && (
                <AICoAuthorDetailRow
                    aiCoAuthors={code.aiCoAuthors}
                    percentageAICoAuthored={getPercentageAICoAuthoredForCodeItem(code)}
                />
            )}
            <CodeItem code={code} status={status} />
            <SourceCodeItem code={code} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
                {prItem.createdAt && (
                    <Text type="secondary">
                        Created{' '}
                        <TimeWithConditionalTz
                            triggeredAtLabel={dayjs(prItem.createdAt)
                                .tz(effectiveTimezone)
                                .format(
                                    use24HourTimeFormat ?
                                        convertTo24HourFormat(DatetimeFormat.LongMonthDateTime)
                                    :   DatetimeFormat.LongMonthDateTime,
                                )}
                            triggeredByMoment={dayjs(prItem.createdAt)}
                            use24HourTimeFormat={use24HourTimeFormat}
                            effectiveTimezone={effectiveTimezone}
                            style={{ color: 'inherit' }}
                        />
                    </Text>
                )}
                {prItem.updatedAt && (
                    <Text type="secondary">
                        Updated{' '}
                        <TimeWithConditionalTz
                            triggeredAtLabel={dayjs(prItem.updatedAt)
                                .tz(effectiveTimezone)
                                .format(
                                    use24HourTimeFormat ?
                                        convertTo24HourFormat(DatetimeFormat.LongMonthDateTime)
                                    :   DatetimeFormat.LongMonthDateTime,
                                )}
                            triggeredByMoment={dayjs(prItem.updatedAt)}
                            use24HourTimeFormat={use24HourTimeFormat}
                            effectiveTimezone={effectiveTimezone}
                            style={{ color: 'inherit' }}
                        />
                    </Text>
                )}
                {commitItem.committedAt && (
                    <Text type="secondary">
                        Committed{' '}
                        <TimeWithConditionalTz
                            triggeredAtLabel={dayjs(commitItem.committedAt)
                                .tz(effectiveTimezone)
                                .format(
                                    use24HourTimeFormat ?
                                        convertTo24HourFormat(DatetimeFormat.LongMonthDateTime)
                                    :   DatetimeFormat.LongMonthDateTime,
                                )}
                            triggeredByMoment={dayjs(commitItem.committedAt)}
                            use24HourTimeFormat={use24HourTimeFormat}
                            effectiveTimezone={effectiveTimezone}
                            style={{ color: 'inherit' }}
                        />
                    </Text>
                )}
            </div>
        </CollapsibleSection>
    );
};

interface CodeItemProps {
    code: CommitItem | PRItem;
    status: FeatureItemCodeStatus;
}

const CodeItem = (props: CodeItemProps) => {
    const { code, status } = props;
    let title = 'PR';
    let hashSign = '#';
    if (isCommit(code)) {
        title = 'Commit';
        hashSign = '';
    }
    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    color: antGrey.grey8,
                }}
            >
                <Text style={{ color: antGrey.grey8 }}>{title}</Text>
                <Space size="small">
                    {iconForStatus(status, code)}
                    <Link href={code.url} target="_blank" style={{ whiteSpace: 'nowrap', marginLeft: '0px' }}>
                        {`${hashSign}${code.identifier}`}
                    </Link>
                </Space>
            </div>
            <Divider style={{ margin: '16px 0px' }} />
        </>
    );
};

interface SourceCodeItemProps {
    code: CodeItemType;
}

const SourceCodeItem = (props: SourceCodeItemProps) => {
    const { code } = props;

    let source: string;
    let title: string;
    let url: string;
    let stackInfo: PullRequestStackInfo;
    if (code.sourcePR !== null) {
        source = `#${code.sourcePR.number}`;
        title = 'Source PR';
        url = code.sourcePR.url;
        stackInfo = code.sourcePR.stackInfo;
    } else if (code.sourceCommit !== null) {
        source = code.sourceCommit.hash;
        title = 'Source Commit';
        url = code.sourceCommit.url;
    } else {
        return null;
    }

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    color: antGrey.grey8,
                }}
            >
                <Text style={{ color: antGrey.grey8 }}>{title}</Text>
                <Space>
                    {stackInfo && (
                        <Tooltip title={stackInfoTooltipText(stackInfo)}>
                            <span style={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                <StackedIcon width={14} height={14} />
                                {stackInfo.position > 0 && stackInfo.size > 0 && (
                                    <Text style={{ color: antGrey.grey8, marginLeft: 6 }}>
                                        {stackInfo.position}/{stackInfo.size}
                                    </Text>
                                )}
                            </span>
                        </Tooltip>
                    )}
                    <div>
                        {
                            <div key={source}>
                                <PullRequestOutlined />
                                <Link href={url} target="_blank" style={{ whiteSpace: 'nowrap', marginLeft: '6px' }}>
                                    {source}
                                </Link>
                            </div>
                        }
                    </div>
                </Space>
            </div>
            <Divider style={{ margin: '16px 0px' }} />
        </>
    );
};

interface StabilityInfoProps {
    stabilityInstallationIds?: IntegrationInstallationId[];
    frItem: FeatureItem;
    buildsWithStabilityInfo?: AppStoreAvailableBuild[];
    releaseId: string;
    appId: string;
    closeDrawer: () => void;
    onApplyFilters: (filters: AnyFilter[]) => void;
}

const StabilityInfo = (props: StabilityInfoProps) => {
    const { frItem, stabilityInstallationIds, buildsWithStabilityInfo, releaseId, onApplyFilters, closeDrawer } = props;
    const stabilityIntegrationIdsByInstallationId = useSelector((state: RootState) => {
        const integrations = selectStabilityIntegrations(state, props.appId);
        return integrations.reduce<Record<string, IntegrationType>>((acc, integration) => {
            acc[integration.installationId] = integration.id;
            return acc;
        }, {});
    });
    const dispatch = useDispatch();

    const isComplete = useLifecycleStepCompleted(LifecycleStep.Release, releaseId);

    const allInstallationIds = stabilityInstallationIds ?? [];
    const allAssociatedIssues = frItem.associatedStabilityIssues ?? [];
    const issuesByInstallationId = allAssociatedIssues.reduce<Record<string, AssociatedStabilityIssue[]>>(
        (acc, issue) => {
            const installationId = issue.stabilityIssue.integrationInstallationId ?? '';
            if (!acc[installationId]) {
                acc[installationId] = [];
            }
            acc[installationId].push(issue);
            return acc;
        },
        {},
    );

    const unattributedIssues: AssociatedStabilityIssue[] = [...(issuesByInstallationId[''] ?? [])];
    for (const [installationId, issues] of Object.entries(issuesByInstallationId)) {
        if (installationId === '') continue;
        if (!stabilityIntegrationIdsByInstallationId[installationId]) {
            unattributedIssues.push(...issues);
        }
    }

    const groupKeys: { key: string; installationId: string | null }[] = allInstallationIds.map((installationId) => ({
        key: installationId,
        installationId,
    }));
    if (unattributedIssues.length > 0 || groupKeys.length === 0) {
        groupKeys.push({ key: '__unattributed__', installationId: null });
    }

    const hasMultipleBuilds = isComplete && buildsWithStabilityInfo?.length > 1;

    const filterByIssueId = (issue: AssociatedStabilityIssue) => {
        const type = FilterType.AssociatedIssues as FilterType.AssociatedIssues;
        const filter = {
            type,
            associatedIssueId: issue.stabilityIssue.issueID,
            associatedIssueShortDescription: issue.stabilityIssue.crashShortDescription,
            buildId: issue.buildIdentifier,
            isFilteredByBuild: false,
        };
        dispatch(updateFeatureItemsFilters({ filters: [filter], appId: props.appId }));
        onApplyFilters([filter]);
        closeDrawer();
    };

    const renderIssuesCard = (
        key: string,
        integrationId: IntegrationType | undefined,
        issuesForCard: AssociatedStabilityIssue[],
    ) => {
        const integrationIds = integrationId ? [integrationId] : undefined;
        if (issuesForCard.length === 0) {
            return (
                <CollapsibleSection
                    key={key}
                    label={<SectionHeader header="Stability" integrationIds={integrationIds} />}
                >
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {isComplete ?
                            <>
                                <Text type="secondary">No associated stability issues found.</Text>
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    Runway attempts to match stability issues with associated code work by detecting
                                    recent changes to the files that caused the issues.
                                </Text>
                            </>
                        :   <Text type="secondary">
                                Associated stability issues will appear here after a final build has been released.
                            </Text>
                        }
                    </div>
                </CollapsibleSection>
            );
        }

        const numberOfIssues = issuesForCard.length;
        const multipleIssues = numberOfIssues > 1;
        return (
            <CollapsibleSection key={key} label={<SectionHeader header="Stability" integrationIds={integrationIds} />}>
                <Alert
                    message={`The following issue${multipleIssues ? 's' : ''} may be related to this item of work.`}
                    type="error"
                    banner
                    showIcon
                    icon={<BugFilled style={{ color: runwayColors.statusRed }} />}
                    style={{ marginBottom: 24 }}
                />
                {issuesForCard.map((associatedIssue, idx) => {
                    const associatedBuild = buildsWithStabilityInfo?.find(
                        (build) => build.appStoreBuild.buildNumber === associatedIssue.buildIdentifier,
                    );
                    const otherAssociatedCommitsCount =
                        associatedIssue.stabilityIssue != null ?
                            associatedIssue.stabilityIssue.associatedCommits.length - 1
                        :   0;
                    return (
                        <div key={associatedIssue.buildIdentifier + idx}>
                            <Space direction="vertical" style={{ width: '100%' }} size="large">
                                <div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            flexWrap: 'wrap',
                                            gap: 6,
                                        }}
                                    >
                                        <Link href={associatedIssue.stabilityIssue.url} target="_blank" strong>
                                            {associatedIssue.stabilityIssue.crashShortDescription}
                                        </Link>
                                        {hasMultipleBuilds && associatedBuild && (
                                            <AppStoreBuild
                                                {...associatedBuild}
                                                styleOverrides={{ padding: '4px 10px', fontSize: 12 }}
                                            />
                                        )}
                                    </div>
                                    <Paragraph
                                        style={{ color: antGrey.grey8, marginTop: 12, marginBottom: 0 }}
                                        ellipsis={{ rows: 3, expandable: true, symbol: 'more' }}
                                    >
                                        {associatedIssue.stabilityIssue.crashLongDescription}
                                    </Paragraph>
                                </div>
                                {associatedIssue.stabilityIssue.blameFile &&
                                    associatedIssue.stabilityIssue.blameLine && (
                                        <Text code>
                                            {associatedIssue.stabilityIssue.blameFile}:
                                            {associatedIssue.stabilityIssue.blameLine}
                                        </Text>
                                    )}
                                <div
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                    }}
                                >
                                    <Text style={{ color: antGrey.grey7 }}>Occurrences (Release / Total)</Text>{' '}
                                    <Text style={{ color: antGrey.grey7 }}>
                                        {associatedIssue.stabilityIssue.occurrencesCountInRelease} /{' '}
                                        {associatedIssue.stabilityIssue.totalOccurrencesCount}
                                    </Text>
                                </div>
                                {otherAssociatedCommitsCount > 0 && (
                                    <div
                                        style={{
                                            backgroundColor: antGrey.grey2,
                                            borderRadius: 6,
                                            paddingTop: 12,
                                            paddingBottom: 12,
                                            paddingLeft: 16,
                                            paddingRight: 16,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            gap: 12,
                                        }}
                                    >
                                        <Text style={{ color: antGrey.grey8 }}>
                                            {otherAssociatedCommitsCount} other work item
                                            {otherAssociatedCommitsCount > 1 ? 's were' : ' was'} flagged as being
                                            possibly associated with this issue.
                                        </Text>

                                        <Link
                                            target="_blank"
                                            onClick={() => filterByIssueId(associatedIssue)}
                                            style={{ fontSize: 14 }}
                                        >
                                            View all work items associated with this issue
                                            <ArrowRightOutlined
                                                style={{ padding: '2px 0 0 14px', color: runwayColors.primary }}
                                            />
                                        </Link>
                                    </div>
                                )}
                                {associatedIssue.stabilityIssue.stackTrace &&
                                    associatedIssue.stabilityIssue.stackTrace.length > 0 && (
                                        <Collapse ghost className="collapse-no-padding">
                                            <Collapse.Panel
                                                header={
                                                    <Text
                                                        style={{
                                                            ...titleStyle,
                                                            color: antGrey.grey7,
                                                            fontSize: 12,
                                                            lineHeight: '22px',
                                                        }}
                                                    >
                                                        Stack trace
                                                    </Text>
                                                }
                                                key="1"
                                            >
                                                <List size="small">
                                                    {associatedIssue.stabilityIssue.stackTrace.map((frame, index) => {
                                                        return (
                                                            <List.Item
                                                                key={index}
                                                                style={{
                                                                    borderBottomStyle: 'dashed',
                                                                    borderBlockColor: antGrey.grey5,
                                                                    display: 'flex',
                                                                    paddingLeft: 0,
                                                                    paddingRight: 0,
                                                                }}
                                                            >
                                                                <Text
                                                                    code
                                                                    style={{
                                                                        marginRight: 10,
                                                                        fontSize: 'smaller',
                                                                    }}
                                                                >
                                                                    {frame.package}
                                                                </Text>
                                                                <Text
                                                                    code
                                                                    style={{
                                                                        minWidth: 0,
                                                                        wordBreak: 'break-all',
                                                                    }}
                                                                >
                                                                    {frame.function}
                                                                </Text>
                                                            </List.Item>
                                                        );
                                                    })}
                                                </List>
                                            </Collapse.Panel>
                                        </Collapse>
                                    )}
                            </Space>
                            {numberOfIssues !== idx + 1 && (
                                <Divider style={{ color: antGrey.grey2, borderTopWidth: 2 }} />
                            )}
                        </div>
                    );
                })}
            </CollapsibleSection>
        );
    };

    return (
        <>
            {groupKeys.map(({ key, installationId }) => {
                if (installationId === null) {
                    return renderIssuesCard(key, undefined, unattributedIssues);
                }
                const integrationId = stabilityIntegrationIdsByInstallationId[installationId];
                const issuesForCard = issuesByInstallationId[installationId] ?? [];
                return renderIssuesCard(key, integrationId, issuesForCard);
            })}
        </>
    );
};

interface PersonProps {
    title: string;
    user: string;
}

const Person = (props: PersonProps) => {
    const { title, user } = props;

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    color: antGrey.grey8,
                }}
            >
                <Text style={{ color: antGrey.grey8 }}>{title}</Text>
                <Space>
                    <IntegrationAvatar user={user} size={24} style={{ color: 'white', fontSize: '12px' }}>
                        {user.charAt(0).toUpperCase()}
                    </IntegrationAvatar>
                    <Text style={{ color: antGrey.grey8 }}>{user}</Text>
                </Space>
            </div>
            <Divider style={{ margin: '16px 0px' }} />
        </>
    );
};

interface CustomFieldProps {
    name: string;
    value: string[];
}

const CustomField = (props: CustomFieldProps) => {
    const { name, value } = props;

    return (
        <>
            <div
                style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'baseline',
                    color: antGrey.grey8,
                }}
            >
                <Text style={{ color: antGrey.grey8 }}>{name}</Text>
                <Space size="small">
                    <Text style={{ color: antGrey.grey8 }}>{value.join(', ')}</Text>
                </Space>
            </div>
            <Divider style={{ margin: '16px 0px' }} />
        </>
    );
};

const CollapsibleSection: React.FC<
    React.PropsWithChildren<{
        label: React.ReactNode;
        extra?: React.ReactNode;
        isLast?: boolean;
    }>
> = ({ label, extra, isLast, children }) => (
    <Collapse
        className="triage-collapse"
        ghost
        bordered={false}
        defaultActiveKey={['section']}
        items={[
            {
                key: 'section',
                showArrow: false,
                style: isLast ? undefined : { borderBottom: drawerSectionSeparator },
                label,
                // Interactive extras (links/tags) shouldn't toggle the collapse.
                extra: extra ? <span onClick={(e) => e.stopPropagation()}>{extra}</span> : undefined,
                children: <div style={{ padding: 16 }}>{children}</div>,
            },
        ]}
    />
);

interface SectionHeaderProps {
    header: string;
    integrationId?: IntegrationType;
    integrationIds?: IntegrationType[];
    customIcon?: React.ReactNode;
}

const SectionHeader = (props: SectionHeaderProps) => {
    const { header, integrationId, integrationIds, customIcon } = props;
    const renderedIntegrationIds =
        integrationIds?.length ? integrationIds
        : integrationId ? [integrationId]
        : [];

    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {renderedIntegrationIds.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    {renderedIntegrationIds.map((id) => (
                        <IntegrationIcon key={id} integrationId={id} size={25} />
                    ))}
                </div>
            )}
            {customIcon && customIcon}
            <Text style={{ color: antGrey.grey8, fontWeight: 'normal' }}>{header}</Text>
        </div>
    );
};

interface DescriptionProps {
    description: string;
    drawerIsVisible: boolean;
}

const ExpandableDescription = (props: DescriptionProps) => {
    const { description, drawerIsVisible } = props;

    const [expandDescription, setExpandDescription] = React.useState(false);

    React.useEffect(() => {
        if (!drawerIsVisible) {
            setExpandDescription(false);
        }
    }, [drawerIsVisible]);

    return (
        <>
            {!expandDescription && (
                <Paragraph
                    ellipsis={{
                        rows: 2,
                        expandable: true,
                        symbol: 'Show full',
                        onExpand: () => setExpandDescription(true),
                    }}
                    style={{ marginBottom: '24px' }}
                >
                    {description}
                </Paragraph>
            )}
            {expandDescription && (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-end',
                        marginBottom: '24px',
                        wordBreak: 'break-all',
                    }}
                >
                    <MarkdownComponent source={description} />
                    <Button type="link" style={{ paddingRight: '0px' }} onClick={() => setExpandDescription(false)}>
                        Collapse
                    </Button>
                </div>
            )}
        </>
    );
};

export default FeatureItemDrawer;
